import { useCallback, useRef } from 'react';
import * as Cesium from 'cesium';
import type { GeoFeature } from '../../../types/mission';
import { ACCENT, ACCENT_FILL, BLACK } from '../constants';
import { circlePolylinePositions, geodesicKm } from '../utils/orbitUtils';

type LatLng = { lat: number; lng: number };

const DRAFT_GEOMETRY_UPDATE_MS = 80;
const isValidLatLng = (p: LatLng | null | undefined): p is LatLng =>
  !!p &&
  typeof p.lat === 'number' &&
  typeof p.lng === 'number' &&
  Number.isFinite(p.lat) &&
  Number.isFinite(p.lng);

const unwrapLongitudes = (coords: { lat: number; lng: number }[]): { lat: number; lng: number }[] => {
  if (coords.length === 0) return [];
  const unwrapped = [{ ...coords[0] }];
  for (let i = 1; i < coords.length; i++) {
    const prevLng = unwrapped[i - 1].lng;
    let currLng = coords[i].lng;
    let diff = currLng - prevLng;

    diff = ((diff + 180) % 360);
    if (diff < 0) diff += 360;
    diff -= 180;

    currLng = prevLng + diff;
    unwrapped.push({ lat: coords[i].lat, lng: currLng });
  }
  return unwrapped;
};

export function useGeoFeatures(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  entityGroupMap: React.MutableRefObject<Map<string, string[]>>,
  layersRef: React.MutableRefObject<any>,
  drawingHandlerRef: React.MutableRefObject<Cesium.ScreenSpaceEventHandler | null>,
  polygonVerticesRef: React.MutableRefObject<{ lat: number; lng: number }[]>,
  circleCenterRef: React.MutableRefObject<{ lat: number; lng: number } | null>,
  circleRadiusRef: React.MutableRefObject<number>,
  draftVertexEntityIdsRef: React.MutableRefObject<string[]>,
  draggingVertexIdxRef: React.MutableRefObject<number | null>,
  setLiveDrawingDistanceKm: (distance: number | null) => void,
  pickCartesian: (v: Cesium.Viewer, pos: Cesium.Cartesian2) => Cesium.Cartesian3 | undefined,
  clearGroup: (group: string) => void,
  geoPointImagePropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  geoOutlineColorPropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  geoFillColorPropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  geoFeatureActiveMapRef: React.MutableRefObject<Record<string, boolean>>,
  isSurfacePointCameraFacing: (lon: number, lat: number, alt: number) => boolean,
  accessWindowPairsRef?: React.MutableRefObject<any[]>,
  satIdsRef?: React.MutableRefObject<string[]>,
) {
  const secondaryDrawingHandlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const zoomBlockHandlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const secZoomBlockHandlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);

  const lastHierarchyCoordsRef = useRef<string>('');
  const hadDefaultClickRef = useRef<any>(null);
  const hadDefaultDblClickRef = useRef<any>(null);

  const disableDefaultSelectionAndZoom = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;
    const viewerHandler = (v as any).screenSpaceEventHandler as Cesium.ScreenSpaceEventHandler | undefined;
    if (!viewerHandler) return;

    if (!hadDefaultClickRef.current) {
      hadDefaultClickRef.current = viewerHandler.getInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
      viewerHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }
    if (!hadDefaultDblClickRef.current) {
      hadDefaultDblClickRef.current = viewerHandler.getInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      viewerHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    }
  }, [viewerRef]);

  const restoreDefaultSelectionAndZoom = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;
    const viewerHandler = (v as any).screenSpaceEventHandler as Cesium.ScreenSpaceEventHandler | undefined;
    if (!viewerHandler) return;

    if (hadDefaultClickRef.current) {
      viewerHandler.setInputAction(hadDefaultClickRef.current, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      hadDefaultClickRef.current = null;
    }
    if (hadDefaultDblClickRef.current) {
      viewerHandler.setInputAction(hadDefaultDblClickRef.current, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      hadDefaultDblClickRef.current = null;
    }
  }, [viewerRef]);
  const cachedHierarchyRef = useRef<Cesium.PolygonHierarchy | undefined>(undefined);
  const lastLineCoordsRef = useRef<string>('');
  const cachedLinePositionsRef = useRef<Cesium.Cartesian3[]>([]);
  const floatingCursorRef = useRef<Cesium.Cartesian3 | null>(null);

  const getCachedHierarchy = (points: { lat: number; lng: number }[]) => {
    const key = points.map(p => `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`).join('|');
    if (key === lastHierarchyCoordsRef.current && cachedHierarchyRef.current) {
      return cachedHierarchyRef.current;
    }
    lastHierarchyCoordsRef.current = key;
    const hierarchy = new Cesium.PolygonHierarchy(
      Cesium.Cartesian3.fromDegreesArray(points.flatMap(p => [p.lng, p.lat]))
    );
    cachedHierarchyRef.current = hierarchy;
    return hierarchy;
  };

  const getCachedLinePositions = (points: { lat: number; lng: number }[]) => {
    const key = points.map(p => `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`).join('|');
    if (key === lastLineCoordsRef.current && cachedLinePositionsRef.current) {
      return cachedLinePositionsRef.current;
    }
    lastLineCoordsRef.current = key;
    const positions = Cesium.Cartesian3.fromDegreesArray(points.flatMap(p => [p.lng, p.lat]));
    cachedLinePositionsRef.current = positions;
    return positions;
  };

  const syncDraftEntities = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;

    const validPts = polygonVerticesRef.current.filter(
      (p) => p && typeof p.lat === 'number' && typeof p.lng === 'number' && !isNaN(p.lat) && !isNaN(p.lng)
    );

    // 1. Update/Add Primary Polygon
    let poly = v.entities.getById('__draft_polygon__');
    if (!poly) {
      poly = v.entities.add({
        id: '__draft_polygon__',
        polygon: {
          hierarchy: new Cesium.CallbackProperty(() => {
            const pts = polygonVerticesRef.current.filter(
              (p) => p && typeof p.lat === 'number' && typeof p.lng === 'number' && !isNaN(p.lat) && !isNaN(p.lng)
            );
            if (pts.length < 3) return undefined;
            return getCachedHierarchy(pts);
          }, false) as any,
          material: ACCENT_FILL,
          outline: false,
          height: 0,
          arcType: Cesium.ArcType.GEODESIC,
        },
      });
    }
    poly.show = (validPts.length >= 3);

    // 2. Update/Add Primary Polyline
    let line = v.entities.getById('__draft_polygon_line__');
    if (!line) {
      line = v.entities.add({
        id: '__draft_polygon_line__',
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const pts = polygonVerticesRef.current.filter(
              (p) => p && typeof p.lat === 'number' && typeof p.lng === 'number' && !isNaN(p.lat) && !isNaN(p.lng)
            );
            if (pts.length === 0) return [];

            let linePoints = pts;
            if (floatingCursorRef.current && pts.length > 0) {
              const carto = Cesium.Cartographic.fromCartesian(floatingCursorRef.current);
              if (carto) {
                const curLat = Cesium.Math.toDegrees(carto.latitude);
                const curLng = Cesium.Math.toDegrees(carto.longitude);
                linePoints = [...pts, { lat: curLat, lng: curLng }];
              }
            }

            if (linePoints.length === 1) {
              if (floatingCursorRef.current) {
                return getCachedLinePositions(linePoints);
              }
              return [];
            }
            const ringPoints = [...linePoints, linePoints[0]];
            return getCachedLinePositions(ringPoints);
          }, false) as any,
          width: 1,
          material: ACCENT,
          clampToGround: false,
          arcType: Cesium.ArcType.GEODESIC,
        },
      });
    }
    line.show = (validPts.length >= 1);

    // 3. Do the same for secondary viewer
    const sv = secondaryViewerRef.current;
    if (sv && !sv.isDestroyed()) {
      let polySv = sv.entities.getById('__draft_polygon__');
      if (!polySv) {
        polySv = sv.entities.add({
          id: '__draft_polygon__',
          polygon: {
            hierarchy: new Cesium.CallbackProperty(() => {
              const pts = polygonVerticesRef.current.filter(
                (p) => p && typeof p.lat === 'number' && typeof p.lng === 'number' && !isNaN(p.lat) && !isNaN(p.lng)
              );
              if (pts.length < 3) return undefined;
              return getCachedHierarchy(pts);
            }, false) as any,
            material: ACCENT_FILL,
            outline: false,
            height: 0,
            arcType: Cesium.ArcType.GEODESIC,
          },
        });
      }
      polySv.show = (validPts.length >= 3);

      let lineSv = sv.entities.getById('__draft_polygon_line__');
      if (!lineSv) {
        lineSv = sv.entities.add({
          id: '__draft_polygon_line__',
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              const pts = polygonVerticesRef.current.filter(
                (p) => p && typeof p.lat === 'number' && typeof p.lng === 'number' && !isNaN(p.lat) && !isNaN(p.lng)
              );
              if (pts.length === 0) return [];

              let linePoints = pts;
              if (floatingCursorRef.current && pts.length > 0) {
                const carto = Cesium.Cartographic.fromCartesian(floatingCursorRef.current);
                if (carto) {
                  const curLat = Cesium.Math.toDegrees(carto.latitude);
                  const curLng = Cesium.Math.toDegrees(carto.longitude);
                  linePoints = [...pts, { lat: curLat, lng: curLng }];
                }
              }

              if (linePoints.length === 1) {
                if (floatingCursorRef.current) {
                  return getCachedLinePositions(linePoints);
                }
                return [];
              }
              const ringPoints = [...linePoints, linePoints[0]];
              return getCachedLinePositions(ringPoints);
            }, false) as any,
            width: 1,
            material: ACCENT,
            clampToGround: false,
            arcType: Cesium.ArcType.GEODESIC,
          },
        });
      }
      lineSv.show = (validPts.length >= 1);
    }
  }, [secondaryViewerRef]);

  const stopDraw = useCallback(() => {
    restoreDefaultSelectionAndZoom();
    if (drawingHandlerRef.current) {
      if (!drawingHandlerRef.current.isDestroyed()) {
        drawingHandlerRef.current.destroy();
      }
      drawingHandlerRef.current = null;
    }
    if (secondaryDrawingHandlerRef.current) {
      if (!secondaryDrawingHandlerRef.current.isDestroyed()) {
        secondaryDrawingHandlerRef.current.destroy();
      }
      secondaryDrawingHandlerRef.current = null;
    }
    if (zoomBlockHandlerRef.current) {
      if (!zoomBlockHandlerRef.current.isDestroyed()) {
        zoomBlockHandlerRef.current.destroy();
      }
      zoomBlockHandlerRef.current = null;
    }
    if (secZoomBlockHandlerRef.current) {
      if (!secZoomBlockHandlerRef.current.isDestroyed()) {
        secZoomBlockHandlerRef.current.destroy();
      }
      secZoomBlockHandlerRef.current = null;
    }
    polygonVerticesRef.current = [];
    circleCenterRef.current = null;
    lastHierarchyCoordsRef.current = '';
    cachedHierarchyRef.current = undefined;
    lastLineCoordsRef.current = '';
    cachedLinePositionsRef.current = [];
    floatingCursorRef.current = null;
    const v = viewerRef.current;
    if (!v) return;
    draggingVertexIdxRef.current = null;
    circleCenterRef.current = null;
    circleRadiusRef.current = 0;
    ['__draft_point__', '__draft_polygon__', '__draft_polygon_line__', '__draft_dist_label__', '__draft_circle__', '__draft_circle_outline__'].forEach((id) => {
      const e = v.entities.getById(id);
      if (e) v.entities.remove(e);
    });
    draftVertexEntityIdsRef.current.forEach((id) => {
      const e = v.entities.getById(id);
      if (e) v.entities.remove(e);
    });
    draftVertexEntityIdsRef.current = [];
    setLiveDrawingDistanceKm(null);

    const sv = secondaryViewerRef.current;
    if (sv && !sv.isDestroyed()) {
      ['__draft_point__', '__draft_polygon__', '__draft_polygon_line__', '__draft_dist_label__', '__draft_circle__', '__draft_circle_outline__'].forEach((id) => {
        const e = sv.entities.getById(id);
        if (e) sv.entities.remove(e);
      });
      draftVertexEntityIdsRef.current.forEach((id) => {
        const e = sv.entities.getById(id);
        if (e) sv.entities.remove(e);
      });
    }
  }, [
    restoreDefaultSelectionAndZoom,
    secondaryViewerRef,
    setLiveDrawingDistanceKm,
    viewerRef,
  ]);

  const startPointDraw = useCallback((onPick: (lat: number, lng: number) => void) => {
    const v = viewerRef.current;
    if (!v) return;
    if (drawingHandlerRef.current) { drawingHandlerRef.current.destroy(); }
    if (secondaryDrawingHandlerRef.current) { secondaryDrawingHandlerRef.current.destroy(); }

    const handler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas as HTMLCanvasElement);
    handler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
      const cartesian = pickCartesian(v, movement.position);
      if (!cartesian) return;
      const carto = Cesium.Cartographic.fromCartesian(cartesian);
      if (!carto) return;
      const lat = Cesium.Math.toDegrees(carto.latitude);
      const lng = Cesium.Math.toDegrees(carto.longitude);
      onPick(lat, lng);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    drawingHandlerRef.current = handler;

    const sv = secondaryViewerRef.current;
    if (sv && !sv.isDestroyed()) {
      const secHandler = new Cesium.ScreenSpaceEventHandler(sv.scene.canvas as HTMLCanvasElement);
      secHandler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
        const cartesian = pickCartesian(sv, movement.position);
        if (!cartesian) return;
        const carto = Cesium.Cartographic.fromCartesian(cartesian);
        if (!carto) return;
        const lat = Cesium.Math.toDegrees(carto.latitude);
        const lng = Cesium.Math.toDegrees(carto.longitude);
        onPick(lat, lng);
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      secondaryDrawingHandlerRef.current = secHandler;
    }
  }, [pickCartesian, secondaryViewerRef]);

  const updateDraftPoint = useCallback((lat: number, lng: number) => {
    if (
      typeof lat !== 'number' ||
      typeof lng !== 'number' ||
      isNaN(lat) ||
      isNaN(lng) ||
      !isFinite(lat) ||
      !isFinite(lng)
    ) {
      return;
    }
    const v = viewerRef.current;
    if (!v) return;
    const existing = v.entities.getById('__draft_point__');
    const pos = Cesium.Cartesian3.fromDegrees(lng, lat);
    if (existing) {
      existing.position = new Cesium.ConstantPositionProperty(pos);
    } else {
      v.entities.add({
        id: '__draft_point__',
        position: pos,
        billboard: {
          image: '/assets/point_white.png',
          scale: 1.0,
          heightReference: new Cesium.CallbackProperty(() => {
            const activeV = (window as any).currentRenderingViewer || v;
            if (activeV && activeV.scene.mode !== Cesium.SceneMode.SCENE3D) {
              return Cesium.HeightReference.NONE;
            }
            return Cesium.HeightReference.CLAMP_TO_GROUND;
          }, false),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          eyeOffset: new Cesium.Cartesian3(0.0, 0.0, -10.0),
        },
      });
    }

    const sv = secondaryViewerRef.current;
    if (sv && !sv.isDestroyed()) {
      const existingSv = sv.entities.getById('__draft_point__');
      if (existingSv) {
        existingSv.position = new Cesium.ConstantPositionProperty(pos);
      } else {
        sv.entities.add({
          id: '__draft_point__',
          position: pos,
          billboard: {
            image: '/assets/point_white.png',
            scale: 1.0,
            heightReference: Cesium.HeightReference.NONE,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            eyeOffset: new Cesium.Cartesian3(0.0, 0.0, -10.0),
          },
        });
      }
    }
  }, [viewerRef, secondaryViewerRef]);

  const startPolygonDraw = useCallback((
    onVertexAdded: (vertices: { lat: number; lng: number }[]) => void,
    onDrawFinished?: (vertices: { lat: number; lng: number }[]) => void,
  ) => {
    const v = viewerRef.current;
    if (!v) return;
    const sv = secondaryViewerRef.current;

    // Safely destroy any previous drawing event handlers to prevent memory leaks and events collision
    if (drawingHandlerRef.current) {
      if (!drawingHandlerRef.current.isDestroyed()) {
        drawingHandlerRef.current.destroy();
      }
      drawingHandlerRef.current = null;
    }
    if (secondaryDrawingHandlerRef.current) {
      if (!secondaryDrawingHandlerRef.current.isDestroyed()) {
        secondaryDrawingHandlerRef.current.destroy();
      }
      secondaryDrawingHandlerRef.current = null;
    }
    if (zoomBlockHandlerRef.current) {
      if (!zoomBlockHandlerRef.current.isDestroyed()) {
        zoomBlockHandlerRef.current.destroy();
      }
      zoomBlockHandlerRef.current = null;
    }
    if (secZoomBlockHandlerRef.current) {
      if (!secZoomBlockHandlerRef.current.isDestroyed()) {
        secZoomBlockHandlerRef.current.destroy();
      }
      secZoomBlockHandlerRef.current = null;
    }

    polygonVerticesRef.current = [];
    draftVertexEntityIdsRef.current.forEach((id) => {
      const e = v.entities.getById(id);
      if (e) v.entities.remove(e);
    });
    draftVertexEntityIdsRef.current = [];

    ['__draft_polygon__', '__draft_polygon_line__', '__draft_dist_label__'].forEach((id) => {
      const e = v.entities.getById(id);
      if (e) v.entities.remove(e);
    });
    disableDefaultSelectionAndZoom();

    floatingCursorRef.current = null;
    syncDraftEntities();

    const handler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas as HTMLCanvasElement);
    const zoomBlockHandler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas as HTMLCanvasElement);
    zoomBlockHandlerRef.current = zoomBlockHandler;

    const finishDraw = () => {
      floatingCursorRef.current = null;
      restoreDefaultSelectionAndZoom();
      
      // Make handlers inert immediately so they stop reacting to canvas events
      handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
      handler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_CLICK);
      handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

      zoomBlockHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

      drawingHandlerRef.current = null;
      syncDraftEntities();
      v.scene.requestRender();
      if (sv && !sv.isDestroyed()) sv.scene.requestRender();
      if (onDrawFinished) onDrawFinished([...polygonVerticesRef.current]);
    };

    handler.setInputAction((movement: { endPosition: Cesium.Cartesian2 }) => {
      const vCurrent = viewerRef.current;
      if (!vCurrent) return;
      floatingCursorRef.current = pickCartesian(vCurrent, movement.endPosition) ?? null;
      vCurrent.scene.requestRender();
      const svCurrent = secondaryViewerRef.current;
      if (svCurrent && !svCurrent.isDestroyed()) svCurrent.scene.requestRender();
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
      const cartesian = pickCartesian(v, movement.position);
      if (!cartesian) return;
      const carto = Cesium.Cartographic.fromCartesian(cartesian);
      if (!carto) return;
      polygonVerticesRef.current = [
        ...polygonVerticesRef.current,
        {
          lat: Cesium.Math.toDegrees(carto.latitude),
          lng: Cesium.Math.toDegrees(carto.longitude),
        },
      ];
      const pt = polygonVerticesRef.current[polygonVerticesRef.current.length - 1];
      const vEntity = v.entities.add({
        id: `__edit_vertex_${polygonVerticesRef.current.length - 1}__`,
        position: Cesium.Cartesian3.fromDegrees(pt.lng, pt.lat),
        point: {
          pixelSize: 10,
          color: Cesium.Color.WHITE,
          outlineColor: ACCENT,
          outlineWidth: 2,
          disableDepthTestDistance: 100000.0,
        },
      });
      draftVertexEntityIdsRef.current.push(vEntity.id);

      // Do the same for secondary viewer
      if (sv && !sv.isDestroyed()) {
        sv.entities.add({
          id: `__edit_vertex_${polygonVerticesRef.current.length - 1}__`,
          position: Cesium.Cartesian3.fromDegrees(pt.lng, pt.lat),
          point: {
            pixelSize: 10,
            color: Cesium.Color.WHITE,
            outlineColor: ACCENT,
            outlineWidth: 2,
            disableDepthTestDistance: 100000.0,
          },
        });
      }

      syncDraftEntities();
      v.scene.requestRender();
      if (sv && !sv.isDestroyed()) sv.scene.requestRender();

      onVertexAdded([...polygonVerticesRef.current]);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction(() => {
      finishDraw();
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

    zoomBlockHandler.setInputAction(() => {
      if (polygonVerticesRef.current.length > 0) {
        polygonVerticesRef.current = polygonVerticesRef.current.slice(0, -1);
        const lastEntityId = draftVertexEntityIdsRef.current.pop();
        if (lastEntityId) {
          const e = v.entities.getById(lastEntityId);
          if (e) v.entities.remove(e);
          if (sv && !sv.isDestroyed()) {
            const eSv = sv.entities.getById(lastEntityId);
            if (eSv) sv.entities.remove(eSv);
          }
        }
      }
      finishDraw();
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    drawingHandlerRef.current = handler;

    if (sv && !sv.isDestroyed()) {
      const secHandler = new Cesium.ScreenSpaceEventHandler(sv.scene.canvas as HTMLCanvasElement);
      const secZoomBlockHandler = new Cesium.ScreenSpaceEventHandler(sv.scene.canvas as HTMLCanvasElement);
      secZoomBlockHandlerRef.current = secZoomBlockHandler;

      const secFinishDraw = () => {
        floatingCursorRef.current = null;
        secHandler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        secHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
        secHandler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_CLICK);
        secHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        secZoomBlockHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

        secondaryDrawingHandlerRef.current = null;
        syncDraftEntities();
        v.scene.requestRender();
        if (sv && !sv.isDestroyed()) sv.scene.requestRender();
        if (onDrawFinished) onDrawFinished([...polygonVerticesRef.current]);
      };

      secHandler.setInputAction((movement: { endPosition: Cesium.Cartesian2 }) => {
        const svCurrent = secondaryViewerRef.current;
        if (!svCurrent || svCurrent.isDestroyed()) return;
        floatingCursorRef.current = pickCartesian(svCurrent, movement.endPosition) ?? null;
        svCurrent.scene.requestRender();
        const vCurrent = viewerRef.current;
        if (vCurrent && !vCurrent.isDestroyed()) vCurrent.scene.requestRender();
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      secHandler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
        const cartesian = pickCartesian(sv, movement.position);
        if (!cartesian) return;
        const carto = Cesium.Cartographic.fromCartesian(cartesian);
        if (!carto) return;
        polygonVerticesRef.current = [
          ...polygonVerticesRef.current,
          {
            lat: Cesium.Math.toDegrees(carto.latitude),
            lng: Cesium.Math.toDegrees(carto.longitude),
          },
        ];
        const pt = polygonVerticesRef.current[polygonVerticesRef.current.length - 1];
        const vEntityId = `__edit_vertex_${polygonVerticesRef.current.length - 1}__`;
        sv.entities.add({
          id: vEntityId,
          position: Cesium.Cartesian3.fromDegrees(pt.lng, pt.lat),
          point: {
            pixelSize: 10,
            color: Cesium.Color.WHITE,
            outlineColor: ACCENT,
            outlineWidth: 2,
            disableDepthTestDistance: 100000.0,
          },
        });
        draftVertexEntityIdsRef.current.push(vEntityId);

        // Do the same for primary viewer
        v.entities.add({
          id: vEntityId,
          position: Cesium.Cartesian3.fromDegrees(pt.lng, pt.lat),
          point: {
            pixelSize: 10,
            color: Cesium.Color.WHITE,
            outlineColor: ACCENT,
            outlineWidth: 2,
            disableDepthTestDistance: 100000.0,
          },
        });

        syncDraftEntities();
        v.scene.requestRender();
        if (sv && !sv.isDestroyed()) sv.scene.requestRender();

        onVertexAdded([...polygonVerticesRef.current]);
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      secHandler.setInputAction(() => {
        secFinishDraw();
      }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

      secZoomBlockHandler.setInputAction(() => {
        if (polygonVerticesRef.current.length > 0) {
          polygonVerticesRef.current = polygonVerticesRef.current.slice(0, -1);
          const lastEntityId = draftVertexEntityIdsRef.current.pop();
          if (lastEntityId) {
            const e = sv.entities.getById(lastEntityId);
            if (e) sv.entities.remove(e);
            const ePrim = v.entities.getById(lastEntityId);
            if (ePrim) v.entities.remove(ePrim);
          }
        }
        secFinishDraw();
      }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

      secondaryDrawingHandlerRef.current = secHandler;
    }
  }, [
    disableDefaultSelectionAndZoom,
    pickCartesian,
    restoreDefaultSelectionAndZoom,
    secondaryViewerRef,
    syncDraftEntities,
  ]);

  const updateDraftPolygon = useCallback((vertices: { lat: number; lng: number }[]) => {
    polygonVerticesRef.current = vertices;
    const v = viewerRef.current;
    if (!v) return;

    vertices.forEach((vertex, idx) => {
      if (vertex && typeof vertex.lat === 'number' && typeof vertex.lng === 'number' && !isNaN(vertex.lat) && !isNaN(vertex.lng)) {
        const vtId = `__edit_vertex_${idx}__`;
        const existing = v.entities.getById(vtId);
        if (existing) {
          existing.position = new Cesium.ConstantPositionProperty(
            Cesium.Cartesian3.fromDegrees(vertex.lng, vertex.lat)
          );
        } else {
          v.entities.add({
            id: vtId,
            position: new Cesium.ConstantPositionProperty(
              Cesium.Cartesian3.fromDegrees(vertex.lng, vertex.lat)
            ),
            point: {
              pixelSize: 10,
              color: Cesium.Color.WHITE,
              outlineColor: ACCENT,
              outlineWidth: 2,
              disableDepthTestDistance: 100000.0,
            },
          });
          draftVertexEntityIdsRef.current.push(vtId);
        }
      }
    });

    const toRemove = draftVertexEntityIdsRef.current.filter((id, idx) => idx >= vertices.length);
    toRemove.forEach(id => {
      const e = v.entities.getById(id);
      if (e) v.entities.remove(e);
      draftVertexEntityIdsRef.current = draftVertexEntityIdsRef.current.filter(vid => vid !== id);
    });

    const sv = secondaryViewerRef.current;
    if (sv && !sv.isDestroyed()) {
      vertices.forEach((vertex, idx) => {
        if (vertex && typeof vertex.lat === 'number' && typeof vertex.lng === 'number' && !isNaN(vertex.lat) && !isNaN(vertex.lng)) {
          const vtId = `__edit_vertex_${idx}__`;
          const existing = sv.entities.getById(vtId);
          if (!existing) {
            sv.entities.add({
              id: vtId,
              position: new Cesium.ConstantPositionProperty(
                Cesium.Cartesian3.fromDegrees(vertex.lng, vertex.lat)
              ),
              point: {
                pixelSize: 10,
                color: Cesium.Color.WHITE,
                outlineColor: ACCENT,
                outlineWidth: 2,
                disableDepthTestDistance: 100000.0,
              },
            });
          } else {
            existing.position = new Cesium.ConstantPositionProperty(
              Cesium.Cartesian3.fromDegrees(vertex.lng, vertex.lat)
            );
          }
        }
      });

      toRemove.forEach(id => {
        const e = sv.entities.getById(id);
        if (e) sv.entities.remove(e);
      });
    }

    syncDraftEntities();
    v.scene.requestRender();
    if (sv && !sv.isDestroyed()) sv.scene.requestRender();
  }, [secondaryViewerRef, syncDraftEntities]);

  const startCircleDraw = useCallback((
    onCirclePicked: (centerLat: number, centerLng: number, radiusKm: number) => void
  ) => {
    const v = viewerRef.current;
    if (!v) return;
    if (drawingHandlerRef.current) { drawingHandlerRef.current.destroy(); }
    if (secondaryDrawingHandlerRef.current) { secondaryDrawingHandlerRef.current.destroy(); }
    circleCenterRef.current = null;

    const handler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas as HTMLCanvasElement);

    handler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
      const cartesian = pickCartesian(v, movement.position);
      if (!cartesian) return;
      const carto = Cesium.Cartographic.fromCartesian(cartesian);
      if (!carto) return;
      const centerLat = Cesium.Math.toDegrees(carto.latitude);
      const centerLng = Cesium.Math.toDegrees(carto.longitude);
      circleCenterRef.current = { lat: centerLat, lng: centerLng };

      ['__draft_circle__', '__draft_circle_outline__'].forEach(id => {
        const e = v.entities.getById(id);
        if (e) v.entities.remove(e);
      });
      circleRadiusRef.current = 1;
      v.entities.add({
        id: '__draft_circle__',
        position: Cesium.Cartesian3.fromDegrees(centerLng, centerLat),
        ellipse: { semiMinorAxis: 1, semiMajorAxis: 1, material: ACCENT_FILL },
      });
      v.entities.add({
        id: '__draft_circle_outline__',
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const c = circleCenterRef.current;
            if (!c || circleRadiusRef.current <= 0) return [];
            return circlePolylinePositions(c.lat, c.lng, circleRadiusRef.current);
          }, false) as unknown as Cesium.Cartesian3[],
          width: 2.5,
          material: new Cesium.ColorMaterialProperty(ACCENT),
          clampToGround: true,
        },
      });

      handler.setInputAction((moveEvent: { endPosition: Cesium.Cartesian2 }) => {
        const center = circleCenterRef.current;
        if (!center) return;
        const edgeCartesian = pickCartesian(v, moveEvent.endPosition);
        if (!edgeCartesian) return;
        const edgeCarto = Cesium.Cartographic.fromCartesian(edgeCartesian);
        if (!edgeCarto) return;
        const km = geodesicKm(
          center.lat, center.lng,
          Cesium.Math.toDegrees(edgeCarto.latitude),
          Cesium.Math.toDegrees(edgeCarto.longitude),
        );
        if (km > 0) {
          circleRadiusRef.current = km * 1000;
          const circ = v.entities.getById('__draft_circle__');
          if (circ) {
            (circ.ellipse as Cesium.EllipseGraphics).semiMinorAxis = new Cesium.ConstantProperty(km * 1000);
            (circ.ellipse as Cesium.EllipseGraphics).semiMajorAxis = new Cesium.ConstantProperty(km * 1000);
          }
        }
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      handler.setInputAction((clickEvent: { position: Cesium.Cartesian2 }) => {
        const center = circleCenterRef.current;
        if (!center) return;
        const edgeCartesian = pickCartesian(v, clickEvent.position);
        if (!edgeCartesian) return;
        const edgeCarto = Cesium.Cartographic.fromCartesian(edgeCartesian);
        if (!edgeCarto) return;
        const km = geodesicKm(
          center.lat, center.lng,
          Cesium.Math.toDegrees(edgeCarto.latitude),
          Cesium.Math.toDegrees(edgeCarto.longitude),
        );
        onCirclePicked(center.lat, center.lng, km);
        setTimeout(() => {
          handler.destroy();
        }, 0);
        drawingHandlerRef.current = null;
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction(() => {
      setTimeout(() => {
        handler.destroy();
      }, 0);
      drawingHandlerRef.current = null;
      circleCenterRef.current = null;
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

    drawingHandlerRef.current = handler;
  }, [pickCartesian]);

  const updateDraftCircle = useCallback((centerLat: number, centerLng: number, radiusKm: number) => {
    if (
      typeof centerLat !== 'number' ||
      typeof centerLng !== 'number' ||
      typeof radiusKm !== 'number' ||
      isNaN(centerLat) ||
      isNaN(centerLng) ||
      isNaN(radiusKm) ||
      !isFinite(centerLat) ||
      !isFinite(centerLng) ||
      !isFinite(radiusKm)
    ) {
      return;
    }
    const v = viewerRef.current;
    if (!v) return;
    circleCenterRef.current = { lat: centerLat, lng: centerLng };
    circleRadiusRef.current = Math.max(radiusKm * 1000, 1);
    const pos = Cesium.Cartesian3.fromDegrees(centerLng, centerLat);
    const r = circleRadiusRef.current;
    const existing = v.entities.getById('__draft_circle__');
    if (existing) {
      existing.position = new Cesium.ConstantPositionProperty(pos);
      (existing.ellipse as Cesium.EllipseGraphics).semiMinorAxis = new Cesium.ConstantProperty(r);
      (existing.ellipse as Cesium.EllipseGraphics).semiMajorAxis = new Cesium.ConstantProperty(r);
    } else {
      v.entities.add({
        id: '__draft_circle__',
        position: pos,
        ellipse: { semiMinorAxis: r, semiMajorAxis: r, material: ACCENT_FILL },
      });
      v.entities.add({
        id: '__draft_circle_outline__',
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const c = circleCenterRef.current;
            if (!c || circleRadiusRef.current <= 0) return [];
            return circlePolylinePositions(c.lat, c.lng, circleRadiusRef.current);
          }, false) as unknown as Cesium.Cartesian3[],
          width: 2.5,
          material: new Cesium.ColorMaterialProperty(ACCENT),
          clampToGround: true,
        },
      });
    }
  }, []);

  const startPolygonVertexEdit = useCallback((
    onVertexMoved?: (vertices: { lat: number; lng: number }[]) => void,
    feature?: GeoFeature,
  ) => {
    const v = viewerRef.current;
    if (!v) return;
    const sv = secondaryViewerRef.current;

    if (drawingHandlerRef.current && !drawingHandlerRef.current.isDestroyed()) {
      drawingHandlerRef.current.destroy();
    }
    drawingHandlerRef.current = null;

    disableDefaultSelectionAndZoom();

    const editHandler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas as HTMLCanvasElement);

    editHandler.setInputAction((event: { position: Cesium.Cartesian2 }) => {
      const picked = v.scene.pick(event.position);
      if (!Cesium.defined(picked) || !picked.id) return;
      const entityId: string = typeof picked.id === 'string' ? picked.id : picked.id?.id;
      const idx = draftVertexEntityIdsRef.current.indexOf(entityId);
      if (idx < 0) return;
      draggingVertexIdxRef.current = idx;
      v.scene.screenSpaceCameraController.enableRotate = false;
      v.scene.screenSpaceCameraController.enableZoom = false;
      
      syncDraftEntities();
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    editHandler.setInputAction((event: { endPosition: Cesium.Cartesian2 }) => {
      if (draggingVertexIdxRef.current === null) return;
      const cartesian = pickCartesian(v, event.endPosition);
      if (!cartesian) return;
      const carto = Cesium.Cartographic.fromCartesian(cartesian);
      if (!carto) return;
      const idx = draggingVertexIdxRef.current;
      const newLat = Cesium.Math.toDegrees(carto.latitude);
      const newLng = Cesium.Math.toDegrees(carto.longitude);
      polygonVerticesRef.current = polygonVerticesRef.current.map((pt, i) =>
        i === idx ? { lat: newLat, lng: newLng } : pt
      );

      // Update handle in primary viewer
      const vId = draftVertexEntityIdsRef.current[idx];
      const vEntity = v.entities.getById(vId);
      if (vEntity) {
        vEntity.position = new Cesium.ConstantPositionProperty(
          Cesium.Cartesian3.fromDegrees(newLng, newLat)
        );
      }

      // Update handle in secondary viewer
      if (sv && !sv.isDestroyed()) {
        const svEntity = sv.entities.getById(vId);
        if (svEntity) {
          svEntity.position = new Cesium.ConstantPositionProperty(
            Cesium.Cartesian3.fromDegrees(newLng, newLat)
          );
        }
      }

      syncDraftEntities();
      v.scene.requestRender();
      if (sv && !sv.isDestroyed()) sv.scene.requestRender();
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    editHandler.setInputAction(() => {
      if (draggingVertexIdxRef.current === null) return;
      draggingVertexIdxRef.current = null;
      v.scene.screenSpaceCameraController.enableRotate = true;
      v.scene.screenSpaceCameraController.enableZoom = true;
      restoreDefaultSelectionAndZoom();
      syncDraftEntities();
      v.scene.requestRender();
      if (sv && !sv.isDestroyed()) sv.scene.requestRender();
      if (onVertexMoved) onVertexMoved([...polygonVerticesRef.current]);
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    drawingHandlerRef.current = editHandler;
  }, [pickCartesian, syncDraftEntities, disableDefaultSelectionAndZoom, restoreDefaultSelectionAndZoom, secondaryViewerRef]);

  const loadPolygonForEdit = useCallback((coords: any, onVertexMoved?: (v: any) => void) => {
    const v = viewerRef.current;
    if (!v) return;
    const sv = secondaryViewerRef.current;

    if (drawingHandlerRef.current && !drawingHandlerRef.current.isDestroyed()) {
      drawingHandlerRef.current.destroy();
    }
    drawingHandlerRef.current = null;

    disableDefaultSelectionAndZoom();

    polygonVerticesRef.current = coords.map((vt: any) => ({ lat: vt.lat, lng: vt.lng }));
    draggingVertexIdxRef.current = null;

    draftVertexEntityIdsRef.current.forEach(id => {
      const e = v.entities.getById(id);
      if (e) v.entities.remove(e);
      if (sv && !sv.isDestroyed()) {
        const eSv = sv.entities.getById(id);
        if (eSv) sv.entities.remove(eSv);
      }
    });
    draftVertexEntityIdsRef.current = [];

    ['__draft_polygon__', '__draft_polygon_line__', '__draft_dist_label__'].forEach(id => {
      const e = v.entities.getById(id);
      if (e) v.entities.remove(e);
      if (sv && !sv.isDestroyed()) {
        const eSv = sv.entities.getById(id);
        if (eSv) sv.entities.remove(eSv);
      }
    });

    syncDraftEntities();

    coords.forEach((vertex: any, idx: number) => {
      const vtId = `__edit_vertex_${idx}__`;
      v.entities.add({
        id: vtId,
        position: new Cesium.ConstantPositionProperty(
          Cesium.Cartesian3.fromDegrees(vertex.lng, vertex.lat)
        ),
        point: {
          pixelSize: 10,
          color: Cesium.Color.WHITE,
          outlineColor: ACCENT,
          outlineWidth: 2,
          disableDepthTestDistance: 100000.0,
        },
      });
      draftVertexEntityIdsRef.current.push(vtId);

      if (sv && !sv.isDestroyed()) {
        sv.entities.add({
          id: vtId,
          position: new Cesium.ConstantPositionProperty(
            Cesium.Cartesian3.fromDegrees(vertex.lng, vertex.lat)
          ),
          point: {
            pixelSize: 10,
            color: Cesium.Color.WHITE,
            outlineColor: ACCENT,
            outlineWidth: 2,
            disableDepthTestDistance: 100000.0,
          },
        });
      }
    });

    const editHandler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas as HTMLCanvasElement);

    editHandler.setInputAction((event: { position: Cesium.Cartesian2 }) => {
      const picked = v.scene.pick(event.position);
      if (!Cesium.defined(picked) || !picked.id) return;
      const entityId: string = typeof picked.id === 'string' ? picked.id : picked.id?.id;
      const idx = draftVertexEntityIdsRef.current.indexOf(entityId);
      if (idx < 0) return;
      draggingVertexIdxRef.current = idx;
      v.scene.screenSpaceCameraController.enableRotate = false;
      v.scene.screenSpaceCameraController.enableZoom = false;

      syncDraftEntities();
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    let pendingLoadEditMovement: { endPosition: Cesium.Cartesian2 } | null = null;
    let loadEditUpdateScheduled = false;

    editHandler.setInputAction((event: { endPosition: Cesium.Cartesian2 }) => {
      pendingLoadEditMovement = event;
      if (loadEditUpdateScheduled) return;
      loadEditUpdateScheduled = true;

      requestAnimationFrame(() => {
        loadEditUpdateScheduled = false;
        if (!pendingLoadEditMovement) return;
        const currentEvent = pendingLoadEditMovement;
        pendingLoadEditMovement = null;

        if (draggingVertexIdxRef.current === null) return;
        const cartesian = pickCartesian(v, currentEvent.endPosition);
        if (!cartesian) return;
        const carto = Cesium.Cartographic.fromCartesian(cartesian);
        if (!carto) return;
        const idx = draggingVertexIdxRef.current;
        const newLat = Cesium.Math.toDegrees(carto.latitude);
        const newLng = Cesium.Math.toDegrees(carto.longitude);
        polygonVerticesRef.current = polygonVerticesRef.current.map((pt, i) =>
          i === idx ? { lat: newLat, lng: newLng } : pt
        );

        // Update handle in primary viewer
        const vId = draftVertexEntityIdsRef.current[idx];
        const vEntity = v.entities.getById(vId);
        if (vEntity) {
          vEntity.position = new Cesium.ConstantPositionProperty(
            Cesium.Cartesian3.fromDegrees(newLng, newLat)
          );
        }

        // Update handle in secondary viewer
        if (sv && !sv.isDestroyed()) {
          const svEntity = sv.entities.getById(vId);
          if (svEntity) {
            svEntity.position = new Cesium.ConstantPositionProperty(
              Cesium.Cartesian3.fromDegrees(newLng, newLat)
            );
          }
        }
      });
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    editHandler.setInputAction(() => {
      if (draggingVertexIdxRef.current === null) return;
      draggingVertexIdxRef.current = null;
      v.scene.screenSpaceCameraController.enableRotate = true;
      v.scene.screenSpaceCameraController.enableZoom = true;
      restoreDefaultSelectionAndZoom();
      if (onVertexMoved) onVertexMoved([...polygonVerticesRef.current]);
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    drawingHandlerRef.current = editHandler;
  }, [pickCartesian, syncDraftEntities, disableDefaultSelectionAndZoom, restoreDefaultSelectionAndZoom, secondaryViewerRef]);

  const commitGeoFeature = useCallback((feature: GeoFeature) => {
    const ds = sharedDataSourceRef.current;
    if (!ds) return;

    // Set visibility state for this feature
    geoFeatureActiveMapRef.current[feature.id] = feature.active !== false;

    // Remove any existing committed entity for this id
    [feature.id, `${feature.id}_outline`].forEach((id) => {
      const e = ds.entities.getById(id);
      if (e) ds.entities.remove(e);
    });
    const prevIds = entityGroupMap.current.get('geofeatures') ?? [];
    entityGroupMap.current.set(
      'geofeatures',
      prevIds.filter((id) => id !== feature.id && id !== `${feature.id}_outline`),
    );

    // Remove all draft shells
    stopDraw();

    const geoIds = entityGroupMap.current.get('geofeatures') ?? [];
    const isVisible = () =>
      layersRef.current.geofeatures && (geoFeatureActiveMapRef.current[feature.id] !== false);

    const hasActiveLOS = (time: Cesium.JulianDate, geoId: string) => {
      if (!layersRef.current.los) return false;
      const list = accessWindowPairsRef?.current || [];
      const satIds = satIdsRef?.current || [];
      if (list.length === 0 || satIds.length === 0) return false;
      const epoch = Cesium.JulianDate.toDate(time).getTime() / 1000;
      return list.some(pair => {
        if (pair.status !== 'ok') return false;
        if (pair.counterpartyKind !== 'geo_feature_geofence') return false;
        if (pair.counterpartyId !== geoId) return false;
        if (!satIds.includes(pair.satelliteId)) return false;
        return pair.passes?.some((pass: any) => epoch >= pass.start && epoch <= pass.end);
      });
    };

    if (feature.kind === 'point' && feature.lat != null && feature.lng != null) {
      const pointImageProp = new Cesium.ConstantProperty('/assets/point_white.png');
      geoPointImagePropsRef.current.set(feature.id, pointImageProp);
      const entity = ds.entities.add({
        id: feature.id,
        name: feature.name,
        position: Cesium.Cartesian3.fromDegrees(feature.lng, feature.lat),
        show: feature.active !== false,
        billboard: {
          image: pointImageProp,
          scale: 1.0,
          heightReference: new Cesium.CallbackProperty(() => {
            const v = (window as any).currentRenderingViewer || viewerRef.current;
            if (v && v.scene.mode !== Cesium.SceneMode.SCENE3D) {
              return Cesium.HeightReference.NONE;
            }
            return Cesium.HeightReference.CLAMP_TO_GROUND;
          }, false),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          eyeOffset: new Cesium.Cartesian3(0.0, 0.0, -10.0),
          show: new Cesium.CallbackProperty(
            () => isVisible() && isSurfacePointCameraFacing(feature.lng ?? 0, feature.lat ?? 0, 0),
            false,
          ),
        },
        label: {
          text: feature.name,
          font: '10px "Fira Mono", monospace',
          fillColor: Cesium.Color.WHITE,
          outlineColor: BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -12),
          eyeOffset: new Cesium.Cartesian3(0.0, 0.0, -10.0),
          show: new Cesium.CallbackProperty(
            () => isVisible() && layersRef.current.labels && isSurfacePointCameraFacing(feature.lng ?? 0, feature.lat ?? 0, 0),
            false,
          ),
        },
      });
      geoIds.push(entity.id);
    } else if (feature.kind === 'polygon' && feature.coordinates && feature.coordinates.length >= 3) {
      const degreesArray = feature.coordinates.flatMap((c: any) => [c.lng, c.lat]);
      const fillColorProp = new Cesium.ConstantProperty(Cesium.Color.WHITE.withAlpha(0.1));
      geoFillColorPropsRef.current.set(feature.id, fillColorProp);
      const centerLat = feature.coordinates.reduce((sum: number, c: any) => sum + c.lat, 0) / feature.coordinates.length;
      const centerLng = feature.coordinates.reduce((sum: number, c: any) => sum + c.lng, 0) / feature.coordinates.length;
      const entity = ds.entities.add({
        id: feature.id,
        name: feature.name,
        position: Cesium.Cartesian3.fromDegrees(centerLng, centerLat),
        show: feature.active !== false,
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(
            Cesium.Cartesian3.fromDegreesArray(degreesArray)
          ),
          material: new Cesium.ColorMaterialProperty(
            new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (hasActiveLOS(time, feature.id)) {
                return ACCENT.withAlpha(0.25);
              }
              const currentFill = geoFillColorPropsRef.current.get(feature.id);
              if (currentFill) {
                return currentFill.getValue(time) || Cesium.Color.WHITE.withAlpha(0.1);
              }
              return Cesium.Color.WHITE.withAlpha(0.1);
            }, false) as any
          ),
          outline: false,
          height: 0,
          show: new Cesium.CallbackProperty(isVisible, false),
          arcType: Cesium.ArcType.GEODESIC,
        },
        label: {
          text: feature.name,
          font: '12px "Fira Mono", monospace',
          fillColor: Cesium.Color.WHITE,
          outlineColor: BLACK,
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.TOP,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          pixelOffset: new Cesium.Cartesian2(0, 8),
          eyeOffset: new Cesium.Cartesian3(0, 0, 50000),
          show: new Cesium.CallbackProperty(
            () => isVisible() && layersRef.current.labels,
            false,
          ),
        },
      });
      geoIds.push(entity.id);
      const ring = [...feature.coordinates!, feature.coordinates![0]];
      const outlinePositions = Cesium.Cartesian3.fromDegreesArray(ring.flatMap((c: any) => [c.lng, c.lat]));
      const outlineColorProp = new Cesium.ConstantProperty(Cesium.Color.WHITE);
      geoOutlineColorPropsRef.current.set(feature.id, outlineColorProp);
      const outlineEntity = ds.entities.add({
        id: `${feature.id}_outline`,
        show: feature.active !== false,
        polyline: {
          positions: outlinePositions,
          width: 1,
          material: new Cesium.ColorMaterialProperty(
            new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (hasActiveLOS(time, feature.id)) {
                return ACCENT;
              }
              const currentOutline = geoOutlineColorPropsRef.current.get(feature.id);
              if (currentOutline) {
                return currentOutline.getValue(time) || Cesium.Color.WHITE;
              }
              return Cesium.Color.WHITE;
            }, false) as any
          ),
          clampToGround: true,
          show: new Cesium.CallbackProperty(isVisible, false),
          arcType: Cesium.ArcType.GEODESIC,
        },
      });
      geoIds.push(outlineEntity.id);
    } else if (feature.kind === 'circle' && feature.centerLat != null && feature.centerLng != null && feature.radiusKm) {
      const fillColorProp = new Cesium.ConstantProperty(Cesium.Color.WHITE.withAlpha(0.1));
      geoFillColorPropsRef.current.set(feature.id, fillColorProp);
      const entity = ds.entities.add({
        id: feature.id,
        name: feature.name,
        position: Cesium.Cartesian3.fromDegrees(feature.centerLng, feature.centerLat),
        show: feature.active !== false,
        ellipse: {
          semiMinorAxis: feature.radiusKm * 1000,
          semiMajorAxis: feature.radiusKm * 1000,
          material: new Cesium.ColorMaterialProperty(
            new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (hasActiveLOS(time, feature.id)) {
                return ACCENT.withAlpha(0.25);
              }
              const currentFill = geoFillColorPropsRef.current.get(feature.id);
              if (currentFill) {
                return currentFill.getValue(time) || Cesium.Color.WHITE.withAlpha(0.1);
              }
              return Cesium.Color.WHITE.withAlpha(0.1);
            }, false) as any
          ),
          show: new Cesium.CallbackProperty(isVisible, false),
        },
        label: {
          text: feature.name,
          font: '12px "Fira Mono", monospace',
          fillColor: Cesium.Color.WHITE,
          outlineColor: BLACK,
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.TOP,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          pixelOffset: new Cesium.Cartesian2(0, 8),
          eyeOffset: new Cesium.Cartesian3(0, 0, 50000),
          show: new Cesium.CallbackProperty(
            () => isVisible() && layersRef.current.labels,
            false,
          ),
        },
      });
      geoIds.push(entity.id);
      const outlineColorProp = new Cesium.ConstantProperty(Cesium.Color.WHITE);
      geoOutlineColorPropsRef.current.set(feature.id, outlineColorProp);
      const outlineEntity = ds.entities.add({
        id: `${feature.id}_outline`,
        show: feature.active !== false,
        polyline: {
          positions: circlePolylinePositions(feature.centerLat, feature.centerLng, feature.radiusKm * 1000),
          width: 1,
          material: new Cesium.ColorMaterialProperty(
            new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (hasActiveLOS(time, feature.id)) {
                return ACCENT;
              }
              const currentOutline = geoOutlineColorPropsRef.current.get(feature.id);
              if (currentOutline) {
                return currentOutline.getValue(time) || Cesium.Color.WHITE;
              }
              return Cesium.Color.WHITE;
            }, false) as any
          ),
          clampToGround: true,
          show: new Cesium.CallbackProperty(isVisible, false),
        },
      });
      geoIds.push(outlineEntity.id);
    }

    entityGroupMap.current.set('geofeatures', geoIds);
  }, [stopDraw]);

  const removeGeoFeature = useCallback((featureId: string) => {
    const ds = sharedDataSourceRef.current;
    if (!ds) return;
    const toRemove = ds.entities.values.filter((e: any) =>
      e.id?.startsWith(`geo_pt_${featureId}`) ||
      e.id?.startsWith(`geo_poly_${featureId}`) ||
      e.id?.startsWith(`geo_outline_${featureId}`) ||
      e.id?.startsWith(`geo_circle_${featureId}`)
    );
    toRemove.forEach((e: any) => ds.entities.remove(e));

    const rootEntity = ds.entities.getById(featureId);
    if (rootEntity) ds.entities.remove(rootEntity);
    const outlineEntity = ds.entities.getById(`${featureId}_outline`);
    if (outlineEntity) ds.entities.remove(outlineEntity);

    geoPointImagePropsRef.current.delete(featureId);
    geoOutlineColorPropsRef.current.delete(featureId);
    geoFillColorPropsRef.current.delete(featureId);
  }, []);

  return {
    stopDraw,
    startPointDraw,
    updateDraftPoint,
    startPolygonDraw,
    updateDraftPolygon,
    startCircleDraw,
    updateDraftCircle,
    startPolygonVertexEdit,
    loadPolygonForEdit,
    commitGeoFeature,
    removeGeoFeature,
  };
}
